import { useMemo, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useCreateBundleUsage } from "../hooks/use-create-bundle-usage";
import { useUpdateUsageUser } from "../hooks/use-update-usage-user";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { bundleUsageStatus } from "@/pages/users/types/bundle-usage";
import { useActiveBundles } from "@/features/coupons/hooks/use-active-bundles";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { Bundle } from "@/pages/bundles/types/bundle";
import DatePicker from "@/components/common/date-picker";
import { verifyPaymentId } from "../services/verify-payment-id";
import { verifyUsageId } from "../services/verify-usage-id";
import { BundleCombobox } from "./bundle-combobox";

export const bundleUsageSchema = z.object({
  total_session_quantity: z.number().min(1, "Total sessions required"),
  utilized_session_quantity: z.number().min(0, "Utilized sessions required"),
  user_id: z.string().min(1, "User ID is required"),
  status: z.nativeEnum(bundleUsageStatus),
  bundle_id: z.string().min(1, "Bundle ID is required"),
  source_usage_id: z.string().nullable(),
  start_date: z.string().min(1, "Start date is required"),
  end_date: z.string().min(1, "End date is required"),
  payment_id: z.string().min(1, "Payment ID is required"),
});

type BundleUsageFormData = z.infer<typeof bundleUsageSchema>;

export const existingPackSchema = z.object({
  usage_id: z.string().min(1, "Usage ID is required"),
});
type ExistingPackFormData = z.infer<typeof existingPackSchema>;

// ------------------ Props ------------------
interface BundleUsageFormProps {
  initialData?: Partial<BundleUsageFormData> | null;
  onClose: () => void;
  editMode?: boolean;
  userId: string;
}

export default function BundleUsaeForm({
  initialData,
  onClose,
  editMode,
  userId,
}: BundleUsageFormProps) {
  const [mode, setMode] = useState<"new" | "existing" | "couples">("new");

  // ------------------ New Usage Form ------------------
  const form = useForm<BundleUsageFormData>({
    resolver: zodResolver(bundleUsageSchema),
    defaultValues: {
      total_session_quantity: initialData?.total_session_quantity ?? 1,
      utilized_session_quantity: initialData?.utilized_session_quantity ?? 0,
      user_id: userId,
      status: initialData?.status ?? bundleUsageStatus.ACTIVE,
      bundle_id: initialData?.bundle_id ?? "",
      source_usage_id: initialData?.source_usage_id ?? null,
      start_date: initialData?.start_date ?? "",
      end_date: initialData?.end_date ?? "",
      payment_id: initialData?.payment_id ?? "",
    },
  });

  const createBundleUsageMutation = useCreateBundleUsage();
  const isSubmitting = createBundleUsageMutation.isPending;

  // ------------------ Region → Bundle cascade ------------------
  // Region first: bundles are fetched server-side scoped to the picked
  // region's id (the Bundle→Region relation). No client-side matching of the
  // legacy Bundle.region string against Region.code — those vocabularies
  // drifted ('uae' vs 'ae') and an equality filter silently empties the list.
  const { data: regions, isLoading: isRegionsLoading } = useActiveRegions();
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const { data: bundlesData, isLoading: isBundlesLoading } = useActiveBundles({
    regionId: selectedRegionId,
    enabled: !!selectedRegionId,
  });
  const regionBundles: Bundle[] = useMemo(
    () => (selectedRegionId ? (bundlesData?.bundles ?? []) : []),
    [bundlesData, selectedRegionId],
  );

  const handleRegionChange = (regionId: string) => {
    setSelectedRegionId(regionId);
    // A bundle from the previous region is no longer a valid choice.
    if (form.getValues("bundle_id")) {
      form.setValue("bundle_id", "");
    }
  };

  const handleBundleSelect = (bundleId: string) => {
    form.setValue("bundle_id", bundleId, { shouldValidate: true });
    const sessionQuantity = regionBundles.find(
      (b) => b.id === bundleId,
    )?.session_quantity;
    if (sessionQuantity) {
      form.setValue("total_session_quantity", sessionQuantity, {
        shouldValidate: true,
      });
    }
  };
  const [isPaymentVerified, setIsPaymentVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paymentVerificationMessage, setPaymentVerificationMessage] = useState("");
  const [isUsageVerified, setIsUsageVerified] = useState(false);
  const [verifyingUsage, setVerifyingUsage] = useState(false);
  const [usageVerificationMessage, setUsageVerificationMessage] = useState("");

  // ------------------ Existing Pack Form ------------------
  const existingForm = useForm<ExistingPackFormData>({
    resolver: zodResolver(existingPackSchema),
    defaultValues: { usage_id: "" },
  });
  const addExistingPackMutation = useUpdateUsageUser();

  // ------------------ Submit Handlers ------------------
  const handleNewSubmit: SubmitHandler<BundleUsageFormData> = async (data) => {
    try {
      await createBundleUsageMutation.mutateAsync(data);

      toast({
        title: "Success",
        description: `Bundle usage ${
          editMode ? "updated" : "created"
        } successfully`,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save bundle usage",
        variant: "destructive",
      });
    }
  };

  const handleVerifyPayment = async () => {
    const paymentId = form.getValues("payment_id");

    if (!paymentId) {
      toast({ title: "Error", description: "Enter a Payment ID first", variant: "destructive" });
      return;
    }

    try {
      setVerifying(true);
      const res = await verifyPaymentId(paymentId); // ✅ API call
      if (res.success) {
        setIsPaymentVerified(true);
        toast({ title: "Verified", description: "Payment ID verified successfully" });
        setPaymentVerificationMessage(res?.message ?? `Payment ID ${paymentId} is valid.`);
      } else {
        setIsPaymentVerified(false);
        toast({ title: "Error", description: res?.message ?? 'Failed to verify payment ID', variant: "destructive" });
        setPaymentVerificationMessage(res?.message ?? `Failed to verify payment ID`);
      }
    } catch (err) {
      console.error(err);
      setIsPaymentVerified(false);
      toast({ title: "Error", description: "Verification failed", variant: "destructive" });
      setPaymentVerificationMessage("Failed to verify payment");
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyUsage = async () => {
    const usageId = existingForm.getValues("usage_id");

    if (!usageId) {
      toast({ title: "Error", description: "Enter a Usage ID first", variant: "destructive" });
      return;
    }

    try {
      setVerifyingUsage(true);
      const res = await verifyUsageId(usageId);
      if (res.success) {
        setIsUsageVerified(true);
        toast({ title: "Verified", description: res.message });
        setUsageVerificationMessage(res.message);
      } else {
        setIsUsageVerified(false);
        toast({ title: "Error", description: res.message, variant: "destructive" });
        setUsageVerificationMessage(res.message);
      }
    } catch (err) {
      console.error(err);
      setIsUsageVerified(false);
      toast({ title: "Error", description: "Verification failed", variant: "destructive" });
      setUsageVerificationMessage("Failed to verify usage ID");
    } finally {
      setVerifyingUsage(false);
    }
  };


  const handleExistingSubmit: SubmitHandler<ExistingPackFormData> = async (
    data
  ) => {
    try {
      await addExistingPackMutation.mutateAsync({
        source_id: data.usage_id,
        user_id: userId,
      });
      toast({
        title: "Success",
        description: "Existing pack added successfully",
      });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to add existing pack",
        variant: "destructive",
      });
    }
  };

  // ------------------ Render ------------------
  return (
    <div className="space-y-4 p-6 overflow-x-hidden min-w-0">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === "new" ? "default" : "outline"}
          onClick={() => setMode("new")}
        >
          Create New Pack
        </Button>
        <Button
          type="button"
          variant={mode === "existing" ? "default" : "outline"}
          onClick={() => setMode("existing")}
        >
          Add Existing Pack
        </Button>
      </div>

      {/* New Bundle Usage Form */}
      {mode === "new" && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleNewSubmit)} className="space-y-6 p-6">
            {/* Header */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h2 className="text-2xl font-semibold">
                {editMode ? "Edit" : "Add"} Bundle Usage
              </h2>
            </div>
            {/* Session Info */}
            <div className="grid grid-cols-2 gap-4 min-w-0">
              {/* Region gates the bundle list — not part of the payload. */}
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none">
                  Region
                </label>
                <Select
                  value={selectedRegionId}
                  onValueChange={handleRegionChange}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        isRegionsLoading ? "Loading regions..." : "Select a region"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(regions ?? []).map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.display_name} ({region.code.toUpperCase()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <FormField
                control={form.control}
                name="bundle_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bundle</FormLabel>
                    <FormControl>
                      <BundleCombobox
                        bundles={regionBundles}
                        value={field.value}
                        onSelect={handleBundleSelect}
                        disabled={!selectedRegionId || isBundlesLoading}
                        placeholder={
                          !selectedRegionId
                            ? "Select a region first"
                            : isBundlesLoading
                              ? "Loading bundles..."
                              : "Select a bundle"
                        }
                        triggerClassName="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="total_session_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Total Sessions</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="utilized_session_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Utilized Sessions</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Identifiers */}
            <div className="grid grid-cols-2 gap-4 min-w-0">

              <FormField
                control={form.control}
                name="payment_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment ID</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input placeholder="Payment ID" {...field} />
                      </FormControl>
                      <Button
                        type="button"
                        onClick={handleVerifyPayment}
                        disabled={verifying}
                        variant={isPaymentVerified ? "ghost" : "default"}
                      >
                        {verifying ? "Verifying..." : isPaymentVerified ? "Verified" : "Verify"}
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {paymentVerificationMessage!="" && !isPaymentVerified && (
                <div className="text-red-600 select-text">
                  {paymentVerificationMessage}
                </div>
              )}
              {isPaymentVerified && (
                <div className="text-green-600">
                  {paymentVerificationMessage}
                </div>
              )}

            </div>

            {/* <FormField
              control={form.control}
              name="source_usage_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source Usage ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Source Usage ID (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4 min-w-0">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value || null}
                        onChange={(date) =>
                          field.onChange(date ? date.toISOString() : "")
                        }
                        placeholder="Pick start date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value || null}
                        onChange={(date) =>
                          field.onChange(date ? date.toISOString() : "")
                        }
                        placeholder="Pick end date"
                        disabled={
                          form.watch("start_date")
                            ? { before: new Date(form.watch("start_date")) }
                            : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Status */}
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(bundleUsageStatus).map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />


            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || isPaymentVerified === false}>
                {isSubmitting ? "Saving..." : editMode ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      )}

      {/* Add Existing Pack Form */}
      {mode === "existing" && (
        <Form {...existingForm}>
          <form onSubmit={existingForm.handleSubmit(handleExistingSubmit)}>
            <FormField
              control={existingForm.control}
              name="usage_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Usage ID</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input placeholder="Enter existing Usage ID" {...field} />
                    </FormControl>
                    <Button
                      type="button"
                      onClick={handleVerifyUsage}
                      disabled={verifyingUsage}
                      variant={isUsageVerified ? "ghost" : "default"}
                    >
                      {verifyingUsage ? "Verifying..." : isUsageVerified ? "Verified" : "Verify"}
                    </Button>
                  </div>
                  <FormMessage />
                  {usageVerificationMessage !== "" && !isUsageVerified && (
                    <div className="text-red-600 select-text">{usageVerificationMessage}</div>
                  )}
                  {isUsageVerified && (
                    <div className="text-green-600">{usageVerificationMessage}</div>
                  )}
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addExistingPackMutation.isPending || isUsageVerified === false}
              >
                {addExistingPackMutation.isPending ? "Saving..." : "Add"}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
}
